import { AidaService } from '@adaptiva/ai/common';
import { IProductNavigation } from '@adaptiva/core/app-shell';
import { DisplayValue, filterNullish, TimeService } from '@adaptiva/core/common';
import { ErrorHandlerService } from '@adaptiva/core/error-handler';
import { BooleanEquals, LogicalAnd, NumberEquals } from '@adaptiva/core/filters';
import { Licensing } from '@adaptiva/core/licensing';
import { OverlayService } from '@adaptiva/core/overlays';
import { ListServerSession } from '@adaptiva/core/tables';
import { EBuiltInIds, EJavaClassNames, hasAllRolesPrereq, hasObjectReadPermissionsPrereq, hasSomeProductsPrereq, hasSomeRolesPrereq, IOnboardingState, ObjectSecurityManagerService, OnboardingService, Permissions, PermissionsService } from '@adaptiva/data-service';
import { EAirGapIds } from '@adaptiva/patching/airgap';
import { ApprovalManagerService, EUserApprovalRequestStatus, IUserRequestInfo, PatchOnboarding } from '@adaptiva/patching/common';
import { inject } from '@angular/core';
import { catchError, EMPTY, finalize, interval, map, merge, Observable, of, retry, startWith, switchMap, take } from 'rxjs';

const _productName: string = Licensing.Products.OneSite.PATCH;
const _baseUrl: string = Licensing.PRODUCT_ROUTES.get(_productName);

export const PATCHING_PRODUCT_NAVIGATION: IProductNavigation = {
	productName: _productName,
	route: _baseUrl,
	sidenav: {
		productName: _productName,
		sections: [
			{
				items: [
					{
						display: 'AI-powered Patching',
						icon: 'faSparkles',
						showAsButton: true,
						prerequisites: [
							hasAllRolesPrereq(EBuiltInIds.ROLE_SUPER_ADMIN_ID),
							hasSomeProductsPrereq(Licensing.Products.AddOns.AIP),
						],
						onSelect: () => {
							const _aida: AidaService = inject(AidaService);

							if (!_aida.isEnabled())
								throw new Error('AI features are not enabled');

							_aida.isOpen.set(true);
						}
					}
				]
			},
			{
				title: 'Patch Management',
				items: [
					{
						display: 'Guided Setup',
						icon: 'faQuestionCircle',
						prerequisites: [hasSomeProductsPrereq(Licensing.Products.OneSite.PATCH_EXPRESS)],
						showAsButton: true,
						onSelect(): void {
							const _securityManager: ObjectSecurityManagerService = inject(ObjectSecurityManagerService);
							const _permissionService: PermissionsService = inject(PermissionsService);
							const _overlays: OverlayService = inject(OverlayService);
							const _errorHandler: ErrorHandlerService = inject(ErrorHandlerService);

							_securityManager.getPermissionsForAdminOnClass(EJavaClassNames.PatchExpressAutoRemediation).pipe(
								switchMap((_permissions: Permissions) => {
									if (_permissionService.hasReadPermissions(_permissions))
										return _overlays.load({ overlayId: 'PatchExpressWizardComponent' });

									_overlays.error(
										'Insufficient permissions',
										'User does not have read permissions for Patch Express Auto Remediation objects'
									);

									return EMPTY;
								}),
								take(1)
							).subscribe({
								error: (e: Error): void => {
									_errorHandler.defaultErrorHandler(e, 'PATCHING_PRODUCT_NAVIGATION');
								}
							});
						}
					},
					{
						display: 'Get Started',
						showAsButton: true,
						icon: 'faStar',
						prerequisites: [
							hasSomeProductsPrereq(Licensing.Products.OneSite.PATCH_ENTERPRISE),
							() => {
								const _errorHandler: ErrorHandlerService = inject(ErrorHandlerService);

								return inject(OnboardingService).streamState(Licensing.Products.OneSite.PATCH, PatchOnboarding.Flows.GENERAL).pipe(
									switchMap((_state: IOnboardingState) => of(_state.enabled)),
									catchError((_err: Error) => {
										_errorHandler.defaultErrorHandler(_err, 'PATCHING_PRODUCT_NAVIGATION');

										return of(false);
									})
								);
							}
						],
						onSelect(): void {
							const _onboardingService: OnboardingService = inject(OnboardingService);

							if (!_onboardingService.hasActiveState(Licensing.Products.OneSite.PATCH, PatchOnboarding.Flows.GENERAL))
								_onboardingService.start(Licensing.Products.OneSite.PATCH, PatchOnboarding.Flows.GENERAL);
						}
					},
					{
						display: 'Home',
						route: _baseUrl,
						icon: 'faHome',
					},
					{
						display: 'Software',
						icon: 'faGrid2',
						children: [
							{
								display: 'Patches',
								route: `${_baseUrl}/software/patches`,
								linkedRoutes: [
									`${_baseUrl}/software/patches/:PatchId`,
									`${_baseUrl}/software/products/:PatchId`,
								]
							},
							{
								display: 'Products',
								route: `${_baseUrl}/software/products`,
								linkedRoutes: [`${_baseUrl}/software/products/:ProductId`]
							},
						]
					},
					{
						display: 'Asset Management',
						icon: 'faComputer',
						children: [
							{
								display: 'Devices',
								route: `${_baseUrl}/assets/devices`,
								linkedRoutes: [
									`${_baseUrl}/assets/devices/:DeviceId`,
									`${_baseUrl}/assets/devices/:DeviceId/product/:ProductId`,
								]
							},
							{
								display: 'Business Units',
								route: `${_baseUrl}/assets/business-units`,
								linkedRoutes: [`${_baseUrl}/assets/business-units/edit/:id`],
							},
							{
								display: 'Client Upgrade',
								route: `${_baseUrl}/assets/client-upgrade`,
								prerequisites: [hasObjectReadPermissionsPrereq(EBuiltInIds.CLIENT_UPGRADE_SETTINGS_OBJECT_ID)],
							},
							{
								display: 'Client Installers',
								route: `${_baseUrl}/assets/client-installers`,
							},
						]
					},
					{
						display: 'Deployments',
						icon: 'faCircleUp',
						route: `${_baseUrl}/deployments`,
					},
					{
						display: 'Strategies',
						icon: 'faBolt',
						route: `${_baseUrl}/strategies`,
						linkedRoutes: [`${_baseUrl}/strategies/edit/:id`],
						prerequisites: [hasSomeProductsPrereq(Licensing.Products.OneSite.PATCH_ENTERPRISE)],
					},
					{
						display: 'Auto Remediation',
						route: `${_baseUrl}/auto-remediation`,
						icon: 'faTools',
						linkedRoutes: [
							`${_baseUrl}/auto-remediation/critical`,
							`${_baseUrl}/auto-remediation/high`,
							`${_baseUrl}/auto-remediation/medium`,
							`${_baseUrl}/auto-remediation/low`
						],
						prerequisites: [hasSomeProductsPrereq(Licensing.Products.OneSite.PATCH_EXPRESS)],
					},
					{
						display: 'Patching Preferences',
						route: `${_baseUrl}/patching-preferences`,
						icon: 'faSlidersH',
						linkedRoutes: [`${_baseUrl}/patching-preferences/edit/:id`],
						prerequisites: [hasSomeProductsPrereq(Licensing.Products.OneSite.PATCH_EXPRESS)],
					},
					{
						display: 'Approvals',
						route: `${_baseUrl}/approval-requests`,
						icon: 'faThumbsUp',
						linkedRoutes: [
							`${_baseUrl}/approval-requests/my-requests`,
							`${_baseUrl}/approval-requests/my-requests/:userRequestId`,
							`${_baseUrl}/approval-requests/all-requests`,
							`${_baseUrl}/approval-requests/all-requests/:userRequestId`,
						],
						badge(): Observable<DisplayValue<int> | null> {
							const _approvalManager: ApprovalManagerService = inject(ApprovalManagerService);
							const _interval: int = TimeService.oneMinute * 10;

							return _approvalManager.getRequestsForUser({
								ascending: true,
								sortColumn: 'time',
								objectFilter: new LogicalAnd([
									new NumberEquals('userRequestStatus', EUserApprovalRequestStatus.SENT_NO_RESPONSE),
									new BooleanEquals('userRequestComplete', false),
									new BooleanEquals('superAdminReadOnlyView', false),
								])
							}).pipe(
								switchMap((_session: ListServerSession<IUserRequestInfo>) => {
									const _badge: Observable<DisplayValue<int> | null> = _session.listUpdated.pipe(
										startWith(void 0),
										map(() => new DisplayValue<int>('accent', _session.listSize, `Pending approvals: ${_session.listSize}`)),
									);
									const _heartbeat: Observable<null> = interval(_interval).pipe(
										switchMap(() => _session.listService.keepAlive(_session.listId)),
										map(() => null),
									);

									return merge(_badge, _heartbeat).pipe(
										filterNullish(),
										finalize(() => _session.destroy())
									);
								}),
								retry(1),
							);
						},
					},
					{
						display: 'Air Gap',
						route: `${_baseUrl}/airgap`,
						icon: 'faArrowsLeftRightToLine',
						prerequisites: [
							hasSomeProductsPrereq(Licensing.Products.AddOns.AIRGAP),
							hasSomeRolesPrereq(EBuiltInIds.ROLE_SUPER_ADMIN_ID, EAirGapIds.ROLE_AIR_GAP_ADMIN, EAirGapIds.ROLE_AIR_GAP_OPERATOR)
						],
					},
					{
						display: 'Advanced Settings',
						icon: 'faCog',
						children: [
							{
								display: 'Maintenance Windows',
								route: `${_baseUrl}/maintenance-windows`,
								icon: 'faClock',
								linkedRoutes: [`${_baseUrl}/maintenance-windows/edit/:id`]
							},
							{
								display: 'User Interaction Settings',
								route: `${_baseUrl}/user-interaction-settings`,
								icon: 'faUserCog',
								linkedRoutes: [`${_baseUrl}/user-interaction-settings/edit/:id`]
							},
							{
								display: 'Communication Providers',
								route: `${_baseUrl}/communication-providers`,
								icon: 'faCommentDotsOutline',
								linkedRoutes: [`${_baseUrl}/communication-providers/edit/:id`],
								prerequisites: [hasSomeProductsPrereq(Licensing.Products.OneSite.PATCH_ENTERPRISE)],
							},
							{
								display: 'Customized Products',
								route: `${_baseUrl}/customized-products`,
								icon: 'faFlag',
								linkedRoutes: [`${_baseUrl}/customized-products/edit/:id`]
							},
							{
								display: 'Risk Assessment Settings',
								icon: 'faWrench',
								route: `${_baseUrl}/risk-assessment`,
								prerequisites: [hasSomeProductsPrereq(Licensing.Products.OneSite.PATCH_ENTERPRISE)],
							},
							{
								display: 'Flex Controls',
								icon: 'faCrosshairs',
								children: [
									{
										display: 'Blocklisting',
										children: [
											{
												display: 'Blocked Patches',
												route: `${_baseUrl}/flex-controls/blocklisting/patches`
											},
											{
												display: 'Blocklist Settings',
												route: `${_baseUrl}/flex-controls/blocklisting/settings`
											}
										],
										linkedRoutes: [`${_baseUrl}/flex-controls/blocklisting/patches/edit/:id`]
									},
									{
										display: 'Cycle Operations',
										children: [
											{
												display: 'Patching Cycles',
												route: `${_baseUrl}/flex-controls/cycles/patching`,
												linkedRoutes: [
													`${_baseUrl}/flex-controls/cycles/patching/history`,
													`${_baseUrl}/flex-controls/cycles/patching/running`,
												]
											},
											{
												display: 'Deployment Cycles',
												route: `${_baseUrl}/flex-controls/cycles/deployment`,
												linkedRoutes: [
													`${_baseUrl}/flex-controls/cycles/deployment/history`,
													`${_baseUrl}/flex-controls/cycles/deployment/running`,
												]
											},
											{
												display: 'Rollout Cycles',
												route: `${_baseUrl}/flex-controls/cycles/rollout`,
												linkedRoutes: [
													`${_baseUrl}/flex-controls/cycles/rollout/history`,
													`${_baseUrl}/flex-controls/cycles/rollout/running`,
												]
											}
										],
									},
									{
										display: 'Exceptions',
										icon: 'faNotEqual',
										children: [
											{
												display: 'Patches',
												route: `${_baseUrl}/flex-controls/exceptions/patches`,
												linkedRoutes: [`${_baseUrl}/flex-controls/exceptions/patches/edit/:id`]
											},
											{
												display: 'Products',
												route: `${_baseUrl}/flex-controls/exceptions/products`,
												linkedRoutes: [`${_baseUrl}/flex-controls/exceptions/products/edit/:id`]
											}
										]
									},
									{
										display: 'Pauses',
										route: `${_baseUrl}/flex-controls/global-pause`,
										children: [
											{
												display: 'Global Pause',
												route: `${_baseUrl}/flex-controls/global-pause/big-red-button`,
											},
											{
												display: 'Paused Products',
												route: `${_baseUrl}/flex-controls/global-pause/products`,
											},
											{
												display: 'Paused Patches',
												route: `${_baseUrl}/flex-controls/global-pause/patches`,
											},
											{
												display: 'Paused Cycles',
												route: `${_baseUrl}/flex-controls/global-pause/cycles`,
											},
											{
												display: 'Paused Business Units',
												route: `${_baseUrl}/flex-controls/global-pause/business-units`,
											},
										],
									},
									{
										display: 'Rollbacks',
										route: `${_baseUrl}/flex-controls/rollback`,
									},
									{
										display: 'Requests',
										route: `${_baseUrl}/flex-controls/request`,
										children: [
											{
												display: 'Scan Request',
												route: `${_baseUrl}/flex-controls/request/scan`,
											},
											{
												display: 'Failure Reset Request',
												route: `${_baseUrl}/flex-controls/request/failure-reset`,
											}
										],
									},
								],
							},
							{
								display: 'Intent Schema',
								prerequisites: [hasSomeProductsPrereq(Licensing.Products.OneSite.PATCH_ENTERPRISE)],
								children: [
									{
										display: 'Strategy',
										icon: 'faClipboardList',
										children: [
											{
												display: 'Patching Processes',
												route: `${_baseUrl}/strategy/patching-processes`,
												linkedRoutes: [`${_baseUrl}/strategy/patching-processes/edit/:id`]
											},
											{
												display: 'Strategy Operations',
												route: `${_baseUrl}/strategy/operations`,
												linkedRoutes: [`${_baseUrl}/strategy/operations/:StrategyId`]
											}
										],
										prerequisites: [hasSomeProductsPrereq(Licensing.Products.OneSite.PATCH_ENTERPRISE)],
									},
									{
										display: 'Bots',
										icon: 'faRobot',
										children: [
											{
												display: 'Patch Deployment Bots',
												route: `${_baseUrl}/bots/deployment-bots`,
												linkedRoutes: [`${_baseUrl}/bots/deployment-bots/edit/:id`]
											},
											{
												display: 'Patch Notification Bots',
												route: `${_baseUrl}/bots/notification-bots`,
												linkedRoutes: [`${_baseUrl}/bots/notification-bots/edit/:id`]
											}
										],
										prerequisites: [hasSomeProductsPrereq(Licensing.Products.OneSite.PATCH_ENTERPRISE)],
									},
									{
										display: 'Chains',
										route: `${_baseUrl}/chains`,
										icon: 'faLink',
										children: [
											{
												display: 'Approval Chains',
												route: `${_baseUrl}/chains/approval-chains`,
												linkedRoutes: [
													`${_baseUrl}/chains/approval-chains/edit/:id`
												]
											},
											{
												display: 'Notification Chains',
												route: `${_baseUrl}/chains/notification-chains`,
												linkedRoutes: [
													`${_baseUrl}/chains/notification-chains/edit/:id`
												]
											},
										],
										prerequisites: [hasSomeProductsPrereq(Licensing.Products.OneSite.PATCH_ENTERPRISE)],
									},
									{
										display: 'Patch Scheduling',
										route: `${_baseUrl}/deployment-channels`,
										icon: 'faSignalStream',
										children: [
											{
												display: 'Deployment Channels',
												route: `${_baseUrl}/deployment-channels/channels`,
												linkedRoutes: [
													`${_baseUrl}/deployment-channels/channels/edit/:id`
												]
											},
											{
												display: 'Deployment Channel Processes',
												route: `${_baseUrl}/deployment-channels/processes`,
												linkedRoutes: [
													`${_baseUrl}/deployment-channels/processes/edit/:id`
												]
											}
										],
										prerequisites: [hasSomeProductsPrereq(Licensing.Products.OneSite.PATCH_ENTERPRISE)],
									},
									{
										display: 'Deployment Waves',
										route: `${_baseUrl}/deployment-waves`,
										icon: 'faLayerGroup',
										linkedRoutes: [`${_baseUrl}/deployment-waves/edit/:id`],
										prerequisites: [hasSomeProductsPrereq(Licensing.Products.OneSite.PATCH_ENTERPRISE)],
									},
									{
										display: 'Rollout Processes',
										route: `${_baseUrl}/rollout-processes`,
										linkedRoutes: [`${_baseUrl}/rollout-processes/edit/:id`],
										prerequisites: [hasSomeProductsPrereq(Licensing.Products.OneSite.PATCH_ENTERPRISE)],
									}
								]
							},
						]
					},
					// TODO: When new patching development is the focus, we can re-enable this.
					// {
					// 	display: 'System Health',
					// 	route: `${_baseUrl}/system-health`,
					// 	icon: 'faDisplayMedical',
					// 	children: [
					// 		{
					// 			display: 'Scan Health',
					// 			route: `${_baseUrl}/system-health/scan`,
					// 		},
					// 		{
					// 			display: 'Feed Health',
					// 			route: `${_baseUrl}/system-health/feed`,
					// 		},
					// 		{
					// 			display: 'Deployment Health',
					// 			route: `${_baseUrl}/system-health/deployment`,
					// 		},
					// 	]
					// },
				]
			},
			{
				title: 'Integrations',
				isCollapsed: false,
				items: [
					{
						display: 'Microsoft Defender',
						route: `${_baseUrl}/integrations/defender/access-settings`,
						icon: 'faWindowsDefender',
						linkedRoutes: [`${_baseUrl}/integrations/defender/access-settings`],
						prerequisites: [hasSomeProductsPrereq(Licensing.Products.AddOns.DEFENDER)],
					},
					{
						display: 'SentinelOne',
						route: `${_baseUrl}/integrations/sentinel-one/access-settings`,
						icon: 'faGlobe',
						linkedRoutes: [`${_baseUrl}/integrations/sentinel-one/access-settings`],
						prerequisites: [hasSomeProductsPrereq(Licensing.Products.AddOns.SENTINEL_ONE)]
					},
					{
						display: 'CrowdStrike',
						route: `${_baseUrl}/integrations/falcon/access-settings`,
						linkedRoutes: [`${_baseUrl}/integrations/falcon/access-settings`],
						icon: 'faCrowdStrike',
						prerequisites: [hasSomeProductsPrereq(Licensing.Products.AddOns.FALCON)],
					},
					{
						display: 'Tenable',
						route: `${_baseUrl}/integrations/tenable`,
						icon: 'faTenable',
						children: [
							{
								display: 'Overview',
								route: `${_baseUrl}/integrations/tenable/overview`
							},
							{
								display: 'CVE Coverage',
								route: `${_baseUrl}/integrations/tenable/cves`
							},
							{
								display: 'Access Settings',
								route: `${_baseUrl}/integrations/tenable/access-settings`
							},
						],
						linkedRoutes: [
							`${_baseUrl}/integrations/tenable/cves/:CveId`,
							`${_baseUrl}/integrations/tenable/cves/:CveId/devices/:DeviceId`,
							`${_baseUrl}/integrations/tenable/devices/:DeviceId`,
						],
						prerequisites: [hasSomeProductsPrereq(Licensing.Products.AddOns.TENABLE)],
					},
					{
						display: 'Qualys',
						route: `${_baseUrl}/integrations/qualys`,
						icon: 'faQualys',
						linkedRoutes: [`${_baseUrl}/integrations/qualys/access-settings`],
						prerequisites: [hasSomeProductsPrereq(Licensing.Products.AddOns.QUALYS)]
					},
				]
			},
		],

	},

};
